// OnlineCourses.js - Adam International Academy Documents Version
import React, { useState, useEffect, useRef } from 'react';
import './OnlineCourses.css';
import Course from '../../assets/resources/IMG-20250927-WA0006.jpg';

const OnlineCourses = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('featured');
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  // Intersection Observer for scroll animations - DÜZELTİLDİ
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' } // threshold düşürüldü, rootMargin eklendi
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // TÜM SECTION'LARI BAŞLANGIÇTA GÖRÜNÜR YAP
  useEffect(() => {
    // Sayfa yüklendiğinde tüm section'ları görünür yap
    const allSections = [
      'formats', 'highlights', 'featured', 'catalog', 
      'paths', 'testimonials', 'cta'
    ];
    
    const initialVisible = {};
    allSections.forEach(section => {
      initialVisible[section] = true;
    });
    
    setVisibleSections(initialVisible);
  }, []);

  // Modal body scroll lock
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Course data from Adam International Academy documents
  const courses = [
    // Sustainable Development & SDG Courses
    {
      id: 1,
      title: 'Sustainable Development Goals (SDG 2030) Implementation',
      description: 'Comprehensive course on understanding and implementing the 17 Sustainable Development Goals in organizational strategy and operations.',
      instructor: 'Prof. Dr. Ayşe Demir',
      category: 'sustainable-development',
      duration: '10 weeks',
      level: 'Intermediate',
      rating: 4.9,
      students: 1250,
      price: 299,
      image: Course,
      featured: true,
      multilingual: ['English', 'Arabic', 'Turkish'],
      syllabus: [
        'Introduction to SDG 2030 Agenda',
        'SDG 4: Quality Education',
        'SDG 5: Gender Equality',
        'SDG 8: Decent Work & Economic Growth',
        'SDG 13: Climate Action',
        'Measuring & Reporting SDG Impact',
        'Case Studies in SDG Implementation'
      ]
    },
    {
      id: 2,
      title: 'Multilingual Education & Capacity Development',
      description: 'Learn strategies for designing and delivering effective educational programs in multiple languages, with focus on culturally sensitive approaches.',
      instructor: 'Prof. Dr. Samir Albuny',
      category: 'education',
      duration: '8 weeks',
      level: 'Advanced',
      rating: 4.8,
      students: 890,
      price: 279,
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      featured: true,
      multilingual: ['English', 'Arabic', 'French'],
      syllabus: [
        'Principles of Multilingual Education',
        'Content Adaptation Strategies',
        'Culturally Sensitive Instructional Design',
        'Technology for Multilingual Learning',
        'Assessment in Multiple Languages',
        'Case Studies from Africa & Middle East'
      ]
    },
    {
      id: 3,
      title: 'International Relations & Diplomacy',
      description: 'Master the fundamentals of international relations, diplomatic protocols, and multilateral negotiations with global partners.',
      instructor: 'Prof. Dr. Hamman Adam',
      category: 'international-relations',
      duration: '12 weeks',
      level: 'Advanced',
      rating: 4.9,
      students: 750,
      price: 349,
      image: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      syllabus: [
        'Theories of International Relations',
        'Diplomatic Protocol & Etiquette',
        'Multilateral Negotiations',
        'International Law Fundamentals',
        'Crisis Diplomacy',
        'UN System & International Organizations',
        'Regional Politics: Middle East, Africa, Europe'
      ]
    },
    {
      id: 4,
      title: 'Artificial Intelligence for Sustainable Development',
      description: 'Explore how AI technologies can be leveraged to address development challenges and achieve SDG targets.',
      instructor: 'Prof. Dr. Kevin Zhang',
      category: 'technology',
      duration: '10 weeks',
      level: 'Intermediate',
      rating: 4.7,
      students: 620,
      price: 329,
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      featured: true,
      syllabus: [
        'AI Fundamentals',
        'Machine Learning for Development',
        'AI in Healthcare & Education',
        'Natural Language Processing for Multilingual Applications',
        'Ethical AI & Bias Mitigation',
        'AI for Climate Action',
        'Case Studies in AI for SDGs'
      ]
    },
    {
      id: 5,
      title: 'Project Management for Development Professionals',
      description: 'Comprehensive project management training tailored for international development projects and SDG-aligned initiatives.',
      instructor: 'Prof. Dr. Nabil Bukairy',
      category: 'management',
      duration: '8 weeks',
      level: 'Intermediate',
      rating: 4.8,
      students: 1100,
      price: 269,
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      syllabus: [
        'Project Lifecycle Management',
        'Logical Framework Approach',
        'Stakeholder Analysis',
        'Risk Management in Development',
        'Monitoring & Evaluation',
        'Budgeting & Financial Management',
        'Reporting & Communication'
      ]
    },
    {
      id: 6,
      title: 'Cybersecurity & Data Protection',
      description: 'Essential cybersecurity skills for protecting organizational data and infrastructure in the digital age.',
      instructor: 'Dr. Susan Chen',
      category: 'technology',
      duration: '8 weeks',
      level: 'Intermediate',
      rating: 4.8,
      students: 950,
      price: 299,
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      featured: true,
      syllabus: [
        'Cybersecurity Fundamentals',
        'Network Security',
        'Data Encryption & Protection',
        'Security Risk Assessment',
        'Incident Response',
        'Compliance & Regulations',
        'Security Awareness Training'
      ]
    },
    {
      id: 7,
      title: 'Renewable Energy & Sustainability',
      description: 'Comprehensive overview of renewable energy technologies and their role in sustainable development.',
      instructor: 'Prof. Dr. Maria Garcia',
      category: 'sustainable-development',
      duration: '10 weeks',
      level: 'Intermediate',
      rating: 4.7,
      students: 680,
      price: 319,
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      syllabus: [
        'Solar Energy Systems',
        'Wind Power Technology',
        'Energy Storage Solutions',
        'Grid Integration',
        'Energy Policy & Economics',
        'Sustainable Energy Projects',
        'Case Studies in Renewable Energy'
      ]
    },
    {
      id: 8,
      title: 'International Law & Commercial Arbitration',
      description: 'Advanced course on international legal frameworks and arbitration procedures for commercial disputes.',
      instructor: 'Prof. Dr. Najib Al-Rahman',
      category: 'legal',
      duration: '12 weeks',
      level: 'Advanced',
      rating: 4.9,
      students: 450,
      price: 399,
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      syllabus: [
        'Sources of International Law',
        'International Commercial Contracts',
        'Arbitration Agreements',
        'Arbitration Procedures',
        'Award Enforcement',
        'Investment Arbitration',
        'Case Studies in International Arbitration'
      ]
    },
    {
      id: 9,
      title: 'Women Empowerment & Leadership (LEADWomen)',
      description: 'Specialized program designed to develop women leaders and promote gender equality in organizations.',
      instructor: 'Prof. Dr. Nancy Adams',
      category: 'leadership',
      duration: '8 weeks',
      level: 'Intermediate',
      rating: 4.9,
      students: 890,
      price: 249,
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      featured: true,
      syllabus: [
        'Women in Leadership',
        'Overcoming Barriers',
        'Building Confidence & Presence',
        'Networking & Mentorship',
        'Work-Life Integration',
        'Advocacy & Policy Change',
        'Creating Inclusive Organizations'
      ]
    },
    {
      id: 10,
      title: 'HSE Management (Health, Safety & Environment)',
      description: 'Comprehensive training on occupational health, safety standards, and environmental management systems.',
      instructor: 'Dr. Emily Johnson',
      category: 'management',
      duration: '6 weeks',
      level: 'Intermediate',
      rating: 4.7,
      students: 720,
      price: 229,
      image: 'https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      syllabus: [
        'HSE Management Systems',
        'Risk Assessment',
        'Occupational Health Standards',
        'Environmental Compliance',
        'Emergency Response Planning',
        'Safety Culture Development',
        'Incident Investigation'
      ]
    },
    {
      id: 11,
      title: 'Digital Transformation & Innovation',
      description: 'Strategies for leading digital transformation initiatives and fostering innovation in organizations.',
      instructor: 'Prof. Dr. Michael Brown',
      category: 'technology',
      duration: '8 weeks',
      level: 'Advanced',
      rating: 4.8,
      students: 830,
      price: 289,
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      syllabus: [
        'Digital Strategy Development',
        'Technology Adoption',
        'Innovation Management',
        'Change Leadership',
        'Digital Culture',
        'Emerging Technologies',
        'Digital Transformation Case Studies'
      ]
    },
    {
      id: 12,
      title: 'Economic Development & Policy Analysis',
      description: 'Advanced course on economic development theories, policy formulation, and impact assessment.',
      instructor: 'Prof. Dr. Abdulnasir Munir',
      category: 'economics',
      duration: '12 weeks',
      level: 'Advanced',
      rating: 4.8,
      students: 550,
      price: 359,
      image: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      syllabus: [
        'Development Economics',
        'Policy Formulation',
        'Economic Feasibility Studies',
        'Impact Assessment',
        'Poverty Reduction Strategies',
        'Sustainable Economic Growth',
        'Case Studies in Development Policy'
      ]
    }
  ];

  // Category list from documents
  const categories = [
    { id: 'all', name: 'All Courses', icon: '📚', count: courses.length },
    { id: 'sustainable-development', name: 'Sustainable Development', icon: '🌍', count: 2 },
    { id: 'leadership', name: 'Leadership', icon: '👑', count: 2 },
    { id: 'management', name: 'Management', icon: '💼', count: 2 },
    { id: 'technology', name: 'Technology & AI', icon: '💻', count: 3 },
    { id: 'international-relations', name: 'Intl. Relations', icon: '🌐', count: 1 },
    { id: 'legal', name: 'Legal & Arbitration', icon: '⚖️', count: 1 },
    { id: 'economics', name: 'Economics', icon: '📊', count: 1 }
  ];

  // Learning formats from documents
  const learningFormats = [
    { name: 'Self-Paced eLearning', icon: '⏱️' },
    { name: 'Blended Learning', icon: '🔄' },
    { name: 'MOOCs', icon: '🌍' },
    { name: 'Technical Webinars', icon: '🎥' },
    { name: 'Mobile Learning', icon: '📱' },
    { name: 'Online Tutored', icon: '👨‍🏫' }
  ];

  // Featured courses
  const featuredCourses = courses.filter(course => course.featured);

  // Filter courses by category
  const filteredCourses = activeCategory === 'all' 
    ? courses 
    : courses.filter(course => course.category === activeCategory);

  // Sort courses based on active tab
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    if (activeTab === 'popular') {
      return b.students - a.students;
    } else if (activeTab === 'new') {
      return b.id - a.id; // Assuming higher ID = newer
    }
    return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
  });

  // Modal functions
  const openCourseModal = (course) => {
    setSelectedCourse(course);
    setIsModalOpen(true);
  };

  const closeCourseModal = () => {
    setIsModalOpen(false);
    setSelectedCourse(null);
  };

  return (
    <div className="online-courses">
      {/* Hero Section - Updated with academy branding */}
      <section className="courses-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Adam International Academy e-Learning</h1>
            <p>SDG-aligned, multilingual courses designed for global professionals. Self-paced learning with 24/7 access and international certification.</p>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-number">45+</span>
                <span className="hero-stat-label">Courses</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-number">30+</span>
                <span className="hero-stat-label">Countries</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-number">100+</span>
                <span className="hero-stat-label">Experts</span>
              </div>
            </div>
            <div className="hero-buttons">
              <button className="btn primary">Explore All Courses</button>
              <button className="btn outline">View Learning Formats</button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="floating-card card-1">
              <span>🌍 SDG 4</span>
            </div>
            <div className="floating-card card-2">
              <span>📱 Mobile Learning</span>
            </div>
            <div className="floating-card card-3">
              <span>🌐 Multilingual</span>
            </div>
            <div className="main-card">
              <h3>Learn Without Borders</h3>
              <p>Join 2,500+ global learners</p>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Formats Section - New from documents */}
      <section 
        className="learning-formats"
        ref={el => sectionRefs.current.formats = el}
        data-section="formats"
      >
        <div className={`container ${visibleSections.formats ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Flexible Learning Formats</h2>
            <p>Choose the format that works best for your schedule and learning style</p>
          </div>
          <div className="formats-grid">
            {learningFormats.map((format, index) => (
              <div key={index} className="format-item">
                <div className="format-icon">{format.icon}</div>
                <h3>{format.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Highlights - Updated */}
      <section 
        className="course-highlights"
        ref={el => sectionRefs.current.highlights = el}
        data-section="highlights"
      >
        <div className={`container ${visibleSections.highlights ? 'visible' : ''}`}>
          <div className="highlights-header">
            <h2>Why Choose Adam Academy e-Learning?</h2>
            <p>Our courses are designed to deliver practical skills aligned with global development goals</p>
          </div>
          <div className="highlights-grid">
            <div className="highlight-item">
              <div className="highlight-icon">🌍</div>
              <h3>SDG 2030 Aligned</h3>
              <p>All courses support the Sustainable Development Goals, especially SDG 4: Quality Education</p>
            </div>
            <div className="highlight-item">
              <div className="highlight-icon">🌐</div>
              <h3>Multilingual Access</h3>
              <p>Courses available in English, Arabic, Turkish, and French for global accessibility</p>
            </div>
            <div className="highlight-item">
              <div className="highlight-icon">🎓</div>
              <h3>International Certification</h3>
              <p>Earn certificates recognized by partner universities and international organizations</p>
            </div>
            <div className="highlight-item">
              <div className="highlight-icon">👥</div>
              <h3>Expert Faculty</h3>
              <p>Learn from 100+ international experts from 30 countries</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section 
        className="featured-courses"
        ref={el => sectionRefs.current.featured = el}
        data-section="featured"
      >
        <div className={`container ${visibleSections.featured ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Featured Courses</h2>
            <p>Most popular courses among development professionals</p>
          </div>
          <div className="featured-courses-grid">
            {featuredCourses.map(course => (
              <div key={course.id} className="featured-course-card">
                <div className="online-course-image">
                  <img src={course.image} alt={course.title} loading="lazy" />
                  <div className="course-badge">Featured</div>
                  {course.multilingual && (
                    <div className="language-badge">
                      {course.multilingual.map(lang => (
                        <span key={lang} className="lang-tag">{lang}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="course-content">
                  <span className="course-category">{course.category.replace('-', ' ')}</span>
                  <h3>{course.title}</h3>
                  <p>{course.description.substring(0, 120)}...</p>
                  <div className="course-meta">
                    <span className="instructor">{course.instructor}</span>
                    <div className="course-stats">
                      <span>{course.rating} ★</span>
                      <span>{course.students} students</span>
                    </div>
                  </div>
                  <div className="course-footer">
                    <div className="price">${course.price}</div>
                    <button className="btn primary" onClick={() => openCourseModal(course)}>View Details</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Catalog */}
      <section 
        className="courses-catalog"
        ref={el => sectionRefs.current.catalog = el}
        data-section="catalog"
      >
        <div className={`container ${visibleSections.catalog ? 'visible' : ''}`}>
          <div className="catalog-header">
            <h2>Course Catalog</h2>
            <div className="catalog-tabs">
              <button 
                className={activeTab === 'featured' ? 'active' : ''} 
                onClick={() => setActiveTab('featured')}
                aria-label="Show featured courses"
              >
                Featured
              </button>
              <button 
                className={activeTab === 'popular' ? 'active' : ''} 
                onClick={() => setActiveTab('popular')}
                aria-label="Show most popular courses"
              >
                Most Popular
              </button>
              <button 
                className={activeTab === 'new' ? 'active' : ''} 
                onClick={() => setActiveTab('new')}
                aria-label="Show newest courses"
              >
                Newest
              </button>
            </div>
          </div>

          <div className="category-filter">
            {categories.map(category => (
              <div 
                key={category.id}
                className={`category-pill ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && setActiveCategory(category.id)}
                aria-label={`Filter by ${category.name}`}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-name">{category.name}</span>
                <span className="category-count">({category.count})</span>
              </div>
            ))}
          </div>

          <div className="courses-grid">
            {sortedCourses.map(course => (
              <div key={course.id} className="course-card">
                <div className="card-content">
                  <div className="online-course-image">
                    <img src={course.image} alt={course.title} loading="lazy" />
                    {course.multilingual && (
                      <div className="language-badge-small">
                        {course.multilingual.length} languages
                      </div>
                    )}
                  </div>
                  <div className="course-info">
                    <span className="course-level">{course.level}</span>
                    <h3>{course.title}</h3>
                    <p className="course-description">{course.description.substring(0, 100)}...</p>
                    <div className="course-instructor">
                      <span className="instructor-name">{course.instructor}</span>
                    </div>
                    <div className="course-details">
                      <span>{course.duration}</span>
                      <span>{course.rating} ★</span>
                      <span>{course.students} students</span>
                    </div>
                  </div>
                  <div className="course-action">
                    <div className="price">${course.price}</div>
                    <button className="btn primary" onClick={() => openCourseModal(course)}>Enroll</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Paths - Updated */}
      <section 
        className="learning-paths"
        ref={el => sectionRefs.current.paths = el}
        data-section="paths"
      >
        <div className={`container ${visibleSections.paths ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Specialized Learning Paths</h2>
            <p>Follow curated paths aligned with SDG 2030 and career goals</p>
          </div>
          <div className="paths-container">
            <div className="learning-path">
              <div className="path-header">
                <h3>Sustainable Development Specialist</h3>
                <p>Master SDG implementation and sustainability strategies</p>
              </div>
              <div className="path-courses">
                <span>4 courses</span>
                <span>40 weeks</span>
                <span>Certificate</span>
              </div>
              <button className="btn outline">Explore Path</button>
            </div>
            <div className="learning-path">
              <div className="path-header">
                <h3>Digital Transformation Leader</h3>
                <p>Lead technology-driven organizational change</p>
              </div>
              <div className="path-courses">
                <span>3 courses</span>
                <span>26 weeks</span>
                <span>Certificate</span>
              </div>
              <button className="btn outline">Explore Path</button>
            </div>
            <div className="learning-path">
              <div className="path-header">
                <h3>International Development Professional</h3>
                <p>Comprehensive training for development careers</p>
              </div>
              <div className="path-courses">
                <span>5 courses</span>
                <span>50 weeks</span>
                <span>Diploma</span>
              </div>
              <button className="btn outline">Explore Path</button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Updated */}
      <section 
        className="courses-testimonials"
        ref={el => sectionRefs.current.testimonials = el}
        data-section="testimonials"
      >
        <div className={`container ${visibleSections.testimonials ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>What Our Students Say</h2>
            <p>Hear from professionals who have advanced their careers with Adam Academy</p>
          </div>
          <div className="testimonials-container">
            <div className="testimonial">
              <div className="testimonial-content">
                <div className="quote-icon">"</div>
                <p>The SDG Implementation course gave me practical tools to integrate sustainability into our organization's strategy. The multilingual approach was invaluable for our international team.</p>
              </div>
              <div className="testimonial-author">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80" alt="Ahmed Hassan" loading="lazy" />
                <div>
                  <h4>Ahmed Hassan</h4>
                  <p>Sustainability Director, Cairo</p>
                </div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <div className="quote-icon">"</div>
                <p>As a policy maker, the International Relations course provided exactly what I needed for my work with multilateral organizations. The faculty expertise is outstanding.</p>
              </div>
              <div className="testimonial-author">
                <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80" alt="Fatima Al-Sayed" loading="lazy" />
                <div>
                  <h4>Fatima Al-Sayed</h4>
                  <p>Policy Advisor, UAE</p>
                </div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <div className="quote-icon">"</div>
                <p>The flexible learning format allowed me to complete my certification while working full-time. The mobile learning app made it possible to study anywhere.</p>
              </div>
              <div className="testimonial-author">
                <img src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80" alt="Mehmet Yılmaz" loading="lazy" />
                <div>
                  <h4>Mehmet Yılmaz</h4>
                  <p>Project Manager, Istanbul</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="courses-cta"
        ref={el => sectionRefs.current.cta = el}
        data-section="cta"
      >
        <div className={`container ${visibleSections.cta ? 'visible' : ''}`}>
          <div className="cta-content">
            <h2>Start Your Learning Journey Today</h2>
            <p>Join 2,500+ professionals from 30+ countries who have advanced their careers with Adam Academy</p>
            <div className="cta-buttons">
              <button className="btn primary large">Browse All Courses</button>
              <button className="btn outline large">Request Information</button>
            </div>
            <div className="cta-contact">
              <span>📞 (+90) 380-772631</span>
              <span>✉️ info@adaminternationalacademy.com</span>
            </div>
          </div>
        </div>
      </section>

      {/* Course Modal - Updated with more details */}
      {isModalOpen && selectedCourse && (
        <div className="course-modal">
          <div className="modal-overlay" onClick={closeCourseModal}></div>
          <div className="modal-content">
            <button className="modal-close" onClick={closeCourseModal} aria-label="Close modal">×</button>
            <div className="modal-header">
              <img src={selectedCourse.image} alt={selectedCourse.title} loading="lazy" />
              <div className="modal-title">
                <h2>{selectedCourse.title}</h2>
                <p>By {selectedCourse.instructor}</p>
              </div>
            </div>
            <div className="modal-body">
              <div className="course-details">
                <div className="detail-item">
                  <span className="label">Category:</span>
                  <span className="value">{selectedCourse.category.replace('-', ' ')}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Duration:</span>
                  <span className="value">{selectedCourse.duration}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Level:</span>
                  <span className="value">{selectedCourse.level}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Rating:</span>
                  <span className="value">{selectedCourse.rating} ★ ({selectedCourse.students} students)</span>
                </div>
              </div>
              
              {selectedCourse.multilingual && (
                <div className="course-languages">
                  <h3>Available Languages</h3>
                  <div className="language-tags">
                    {selectedCourse.multilingual.map(lang => (
                      <span key={lang} className="language-tag">{lang}</span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="course-description">
                <h3>About This Course</h3>
                <p>{selectedCourse.description}</p>
              </div>
              
              <div className="course-syllabus">
                <h3>What You'll Learn</h3>
                <ul>
                  {selectedCourse.syllabus.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <div className="course-price">${selectedCourse.price}</div>
              <div className="modal-actions">
                <button className="btn outline">Add to Wishlist</button>
                <button className="btn primary">Enroll Now</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineCourses;